import * as THREE from 'three';
import { SeededRandom } from '../utils/random.js';
import { GEYSERS } from '../utils/constants.js';
import { SurfacePlan } from './SurfacePlan.js';

/* ── Per-biome geyser visual styles ──────────────────────────── */

const GEYSER_STYLES = {
  lava:    { moundColor: [0.01, 0.20, 0.12], particleColor: [0.03, 0.95, 0.55], particleCount: 60, burstHeight: 22 },
  ice:     { moundColor: [0.58, 0.15, 0.60], particleColor: [0.56, 0.30, 0.85], particleCount: 45, burstHeight: 16 },
  ocean:   { moundColor: [0.48, 0.25, 0.30], particleColor: [0.55, 0.60, 0.70], particleCount: 40, burstHeight: 12 },
  rocky:   { moundColor: [0.06, 0.15, 0.28], particleColor: [0.08, 0.20, 0.65], particleCount: 35, burstHeight: 14 },
  crystal: { moundColor: [0.76, 0.30, 0.35], particleColor: [0.80, 0.65, 0.70], particleCount: 45, burstHeight: 18 },
  forest:  { moundColor: [0.10, 0.30, 0.20], particleColor: [0.35, 0.50, 0.55], particleCount: 30, burstHeight: 10 },
  desert:  { moundColor: [0.08, 0.40, 0.40], particleColor: [0.10, 0.55, 0.65], particleCount: 25, burstHeight: 12 },
};

/**
 * Procedural geyser / thermal vent system.
 *
 * Geysers are placed on the planet surface and periodically erupt,
 * shooting particles upward (away from planet center). Each geyser has
 * a deterministic eruption cycle based on its seed, so all clients
 * see the same eruptions at the same time.
 */
export class GeyserSystem {
  constructor() {
    /** @type {{ id: string, position: {x,y,z}, direction: {x,y,z}, seed: number, period: number, duration: number, phase: number, active: boolean }[]} */
    this.geysers = [];
    this.group = new THREE.Group();
    this.group.name = 'geysers';

    /** @type {{ mesh: THREE.Mesh, vel: THREE.Vector3, life: number, maxLife: number }[]} */
    this._particles = [];
    this._particleGeo = null;
  }

  /* ── public API ────────────────────────────────── */

  /**
   * Place geysers grouped into a few "thermal basins".
   *
   * Each basin's footprint is reserved in the shared SurfacePlan, so the
   * eruptions read as distinct geologic features the player can visit
   * rather than random vents mixed into everything else.
   *
   * @param {object}      planetData   – planet config
   * @param {number}      planetRadius – SURFACE_RADIUS × size
   * @param {function}    getHeightFn  – (normalizedDir) => surfaceHeight
   * @param {SurfacePlan} [plan]       – shared surface zoning plan
   * @returns {{ group: THREE.Group, geysers: object[] }}
   */
  generate(planetData, planetRadius, getHeightFn, plan = null) {
    this.clear();

    if (planetData.type === 'gas') {
      return { group: this.group, geysers: this.geysers };
    }

    plan = plan || new SurfacePlan(planetData, planetRadius);
    const rng = new SeededRandom(planetData.seed + 88888);

    const fieldCount = GEYSERS.FIELDS_MIN +
      Math.floor(rng.next() * (GEYSERS.FIELDS_MAX - GEYSERS.FIELDS_MIN + 1));
    const anchors  = plan.pickSpreadDirs(fieldCount, GEYSERS.FIELD_FOOTPRINT, 'geyser', rng);
    const minAngle = GEYSERS.MIN_SPACING / planetRadius;

    let placed = 0;
    for (const anchor of anchors) {
      const perField = GEYSERS.PER_FIELD_MIN +
        Math.floor(rng.next() * (GEYSERS.PER_FIELD_MAX - GEYSERS.PER_FIELD_MIN + 1));
      const fieldDirs = [];

      let fieldPlaced = 0;
      let attempts = 0;
      while (fieldPlaced < perField && attempts < perField * 12) {
        attempts++;
        const dir = plan.sampleNear(anchor, GEYSERS.FIELD_FOOTPRINT * 0.8, rng);

        let tooClose = false;
        for (const d of fieldDirs) {
          if (dir.angleTo(d) < minAngle) { tooClose = true; break; }
        }
        if (tooClose) continue;
        fieldDirs.push(dir.clone());

        const surfaceHeight = getHeightFn(dir);
        const geyserSeed = planetData.seed + placed * 457 + 33;
        const gRng = new SeededRandom(geyserSeed);

        const data = {
          id: `geyser_${planetData.seed}_${placed}`,
          position: { x: dir.x * surfaceHeight, y: dir.y * surfaceHeight, z: dir.z * surfaceHeight },
          direction: { x: dir.x, y: dir.y, z: dir.z },
          seed: geyserSeed,
          period: gRng.range(GEYSERS.MIN_PERIOD, GEYSERS.MAX_PERIOD),
          duration: gRng.range(GEYSERS.MIN_DURATION, GEYSERS.MAX_DURATION),
          phase: gRng.next() * 100, // offset so not all erupt at once
          active: false,
          planetType: planetData.type,
        };
        this.geysers.push(data);

        // Build mound mesh
        const mound = this._createMound(data, planetData.type);
        this.group.add(mound);

        placed++;
        fieldPlaced++;
      }
    }

    return { group: this.group, geysers: this.geysers };
  }

  /**
   * Per-frame update: trigger eruptions and animate particles.
   * @param {number} dt   – delta time
   * @param {number} time – elapsed game time (for deterministic eruption cycles)
   */
  update(dt, time) {
    // Check eruption state for each geyser
    for (const g of this.geysers) {
      const cyclePos = ((time + g.phase) % g.period) / g.period;
      g.active = cyclePos < (g.duration / g.period);

      // Spawn particles when erupting
      if (g.active) {
        this._spawnEruptionParticles(g, dt);
      }
    }

    // Update existing particles
    this._updateParticles(dt);
  }

  clear() {
    this._clearParticles();
    this.group.traverse(child => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) {
        if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
        else child.material.dispose();
      }
    });
    while (this.group.children.length) this.group.remove(this.group.children[0]);
    this.geysers = [];
  }

  /* ── mound mesh ────────────────────────────────── */

  _createMound(data, planetType) {
    const style = GEYSER_STYLES[planetType] || GEYSER_STYLES.rocky;
    const rng = new SeededRandom(data.seed);
    const group = new THREE.Group();

    const dir = new THREE.Vector3(data.direction.x, data.direction.y, data.direction.z);
    const pos = new THREE.Vector3(data.position.x, data.position.y, data.position.z);

    // Mound base
    const moundR = rng.range(2.5, 5.0);
    const moundH = rng.range(1.5, 4.0);
    const moundGeo = new THREE.ConeGeometry(moundR, moundH, 8);
    // Open top (truncate cone effect by scaling top)
    const mat = new THREE.MeshStandardMaterial({
      color: new THREE.Color().setHSL(...style.moundColor),
      roughness: 0.9,
      metalness: 0.05,
    });
    const mound = new THREE.Mesh(moundGeo, mat);
    mound.castShadow = true;
    group.add(mound);

    // Vent opening — glowing disc
    const ventMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color().setHSL(...style.particleColor),
      emissive: new THREE.Color().setHSL(...style.particleColor),
      emissiveIntensity: 0.6,
    });
    const vent = new THREE.Mesh(
      new THREE.CircleGeometry(moundR * 0.35, 8),
      ventMat,
    );
    vent.position.y = moundH * 0.45;
    vent.rotation.x = -Math.PI / 2;
    group.add(vent);

    // Position & orient
    group.position.copy(pos);
    const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    group.quaternion.copy(quat);

    group.name = `geyser-${data.id}`;
    group.userData.geyserData = data;
    return group;
  }

  /* ── particle system ───────────────────────────── */

  _spawnEruptionParticles(geyser, dt) {
    const style = GEYSER_STYLES[geyser.planetType] || GEYSER_STYLES.rocky;
    const spawnRate = style.particleCount * dt; // particles per second × dt
    const toSpawn = Math.floor(spawnRate) + (Math.random() < (spawnRate % 1) ? 1 : 0);

    if (!this._particleGeo) {
      this._particleGeo = new THREE.SphereGeometry(0.35, 4, 3);
    }

    const dir = new THREE.Vector3(geyser.direction.x, geyser.direction.y, geyser.direction.z);
    const pos = new THREE.Vector3(geyser.position.x, geyser.position.y, geyser.position.z);

    for (let i = 0; i < toSpawn; i++) {
      const mat = new THREE.MeshStandardMaterial({
        color: new THREE.Color().setHSL(
          style.particleColor[0],
          style.particleColor[1],
          style.particleColor[2] + (Math.random() - 0.5) * 0.15,
        ),
        emissive: new THREE.Color().setHSL(...style.particleColor),
        emissiveIntensity: 0.9,
        transparent: true,
        opacity: 0.8,
      });

      const mesh = new THREE.Mesh(this._particleGeo, mat);
      mesh.position.copy(pos).addScaledVector(dir, 1.0);

      // Eject roughly upward (away from surface) with some spread
      const vel = dir.clone().multiplyScalar(style.burstHeight * (0.6 + Math.random() * 0.4));
      vel.x += (Math.random() - 0.5) * 2;
      vel.y += (Math.random() - 0.5) * 2;
      vel.z += (Math.random() - 0.5) * 2;

      const life = 0.8 + Math.random() * 0.8;
      this._particles.push({ mesh, vel, life, maxLife: life });

      if (this.group.parent) {
        this.group.parent.add(mesh);
      } else {
        this.group.add(mesh);
      }
    }
  }

  _updateParticles(dt) {
    for (let i = this._particles.length - 1; i >= 0; i--) {
      const p = this._particles[i];
      p.life -= dt;

      if (p.life <= 0) {
        if (p.mesh.parent) p.mesh.parent.remove(p.mesh);
        p.mesh.material.dispose();
        this._particles.splice(i, 1);
        continue;
      }

      // Move + gravity toward center (simplified)
      p.mesh.position.addScaledVector(p.vel, dt);
      // Slow down
      p.vel.multiplyScalar(1 - dt * 1.5);

      // Fade out
      const t = p.life / p.maxLife;
      p.mesh.material.opacity = t * 0.8;
      p.mesh.scale.setScalar(0.5 + t * 0.5);
    }
  }

  _clearParticles() {
    for (const p of this._particles) {
      if (p.mesh.parent) p.mesh.parent.remove(p.mesh);
      p.mesh.material.dispose();
    }
    this._particles = [];
  }
}
